// src/hooks/useNotificationListener.tsx
import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useRouter } from '@tanstack/react-router'
import { supabase } from '@/services/supabase'
import { toast } from 'sonner'
import manifest from '@/lib/manifest/notification.json'
import { resolveRouteFromManifest } from '@/lib/route-manifest'

interface NotificationPayload {
  id: string
  title: string
  body: string
  is_read: boolean
  action?: {
    entityKey: string
    entityData: Record<string, string>
  }
  created_at: string
  recipient_uid: string
  portal_id: string
  org_id: string
  module: string
  priority: string
}

export function useNotificationListener(
  currentUserId: string | undefined,
  currentPortal: string,
  currentOrg: string | undefined
) {
  const router = useRouter()
  const qc = useQueryClient()

  // 🔑 Track initial load to skip first snapshot
  const isInitialLoad = useRef(true)

  useEffect(() => {
    if (!currentUserId || !currentPortal || !currentOrg) return

    const channel = supabase
      .channel(`notifications-rt-${currentUserId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications_data', // ✅ underscore, not hyphen
          filter: `recipient_uid=eq.${currentUserId}`, // ✅ snake_case column
        },
        (payload) => {
          // Skip initial subscription setup
          if (isInitialLoad.current) {
            isInitialLoad.current = false
            return
          }

          const data = payload.new as NotificationPayload

          // Only process notifications for current portal and org
          if (
            data.portal_id !== currentPortal ||
            data.org_id !== currentOrg
          )
            return

          // Refresh queries for instant UI update
          qc.refetchQueries({ queryKey: ['notifications-list'] })
          qc.invalidateQueries({ queryKey: ['notifications-count'] })

          const isMention =
            data.body?.toLowerCase().includes('mentioned') ||
            data.title?.toLowerCase().includes('mention') ||
            data.body?.includes('@')

          // 🔔 Show toast notification instantly
          toast(data.title, {
            description: (
              <span className='block max-w-[240px] truncate text-sm text-muted-foreground'>
                {data.body}
              </span>
            ),
            action: {
              label: 'Open',
              onClick: () => {
                router.navigate({
                  to: resolveRouteFromManifest(manifest, {
                    entityKey: data.action?.entityKey || '',
                    entityData: data.action?.entityData || {},
                  }),
                  params: data.action?.entityData ?? {},
                  state: {
                    openActivity: isMention,
                    activityId: isMention ? data.id : undefined,
                  },
                })
              },
            },
          })
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [currentUserId, currentPortal, currentOrg, qc, router])
}
